                                                              import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { API_WEB_URLS } from "../../constants/constAPI";
import ExcelJS from "exceljs";
import useSession from "../utils/useSession";
import { Fn_AddEditData, Fn_FillListData } from "../../store/Functions";

// ─────────────────────────────────────────────────────────────
// Native browser download helper
// ─────────────────────────────────────────────────────────────
const saveAs = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 300);
};
 
// ─────────────────────────────────────────────────────────────
// Helper formatting functions
// ─────────────────────────────────────────────────────────────
const fmtBool = (v) => {
  if (v === true || v === 1 || v === "1" || String(v).toUpperCase() === "TRUE" || String(v).toUpperCase() === "YES" || String(v).toUpperCase() === "Y") {
    return "YES";
  }
  if (v === false || v === 0 || v === "0" || String(v).toUpperCase() === "FALSE" || String(v).toUpperCase() === "NO" || String(v).toUpperCase() === "N") {
    return "NO";
  }
  return v ?? "";
};

const parseBool = (v) => {
  if (v === true || v === 1 || v === "1") return true;
  const s = String(v).trim().toUpperCase();
  if (s === "TRUE" || s === "YES" || s === "Y") return true;
  return false;
};

const safeDate = (v) => {
  if (!v) return "";
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    if (!isNaN(d.getTime())) v = d;
  }
  if (v instanceof Date) {
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(v).trim();
  return s.substring(0, 10);
};

// Compares fields between uploaded Excel data and existing DB record
const isRecordModified = (excelData, dbRecord, config, foreignMaps) => {
  if (!dbRecord) return true;

  for (const f of config.fields) {
    if (f.key === "Id") continue;

    let excelVal = excelData[f.key];
    let dbVal = dbRecord[f.key];

    // Fallbacks for alternative naming in dbRecord
    if (dbVal === undefined || dbVal === null) {
      if (f.key === "ExtraPayName" && dbRecord.Name !== undefined) dbVal = dbRecord.Name;
      if (f.key === "Name") {
        dbVal = dbRecord.Name || dbRecord.DepartmentName || dbRecord.DesignationName || dbRecord.FirmName || dbRecord.ExtraPayName || dbRecord.BankName;
      }
      if (f.key === "WorkerCategory") {
        dbVal = dbRecord.WorkerCategoryName;
        if (!dbVal && foreignMaps?.WorkerCategoryList && dbRecord.F_WorkerCategory) {
          const cat = foreignMaps.WorkerCategoryList.find((c) => Number(c.Id) === Number(dbRecord.F_WorkerCategory));
          if (cat) dbVal = cat.Name;
        }
      }
      if (f.key === "FirmType") {
        dbVal = dbRecord.FirmTypeName || dbRecord.FirmType;
        if (!dbVal && foreignMaps?.FirmTypeList && dbRecord.F_FirmType) {
          const ft = foreignMaps.FirmTypeList.find((t) => Number(t.Id) === Number(dbRecord.F_FirmType));
          if (ft) dbVal = ft.Name;
        }
      }
      if (f.key === "ConcernType") {
        dbVal = dbRecord.ConcernTypeName || dbRecord.ConcernType;
      }
      if (f.key === "PremisesType") {
        dbVal = dbRecord.PremisesTypeName || dbRecord.PremisesType;
      }
      if (f.key === "Year") {
        dbVal = dbRecord.YearName || dbRecord.Year;
      }
      if (
        f.key === "BranchName" ||
        f.key === "IsMainBranch" ||
        [
          "CoAddress", "CoOtherAddress", "OfficeAddress", "OfficeAddressOther", "PhoneMobileNo",
          "PFNo", "ESINo", "LINNo", "FBRegNo", "RegRenDate", "RegExpiryDate", "Connection",
          "ManagerName", "ManagerAddress", "IsContractor", "PrincipleEmployer", "PrincipleEmployerAddress",
          "PFUserName", "PFPassword", "ESIUserName", "ESIPassword"
        ].includes(f.key)
      ) {
        if (dbRecord.BranchData) {
          try {
            const parsed = typeof dbRecord.BranchData === "string" ? JSON.parse(dbRecord.BranchData) : dbRecord.BranchData;
            if (Array.isArray(parsed) && parsed.length > 0) {
              const matched = parsed.find(
                (b) => String(b.Name || "").trim().toLowerCase() === String(excelData.BranchName || "").trim().toLowerCase()
              );
              if (matched) {
                if (f.key === "BranchName") dbVal = matched.Name;
                else if (f.key === "IsMainBranch") dbVal = matched.IsMainBranch ? "YES" : "NO";
                else if (matched[f.key] !== undefined) dbVal = matched[f.key];
              } else if (f.key === "BranchName") {
                dbVal = parsed[0]?.Name || "Head Office";
              }
            }
          } catch (_) {}
        }
        if (f.key === "BranchName" && !dbVal) dbVal = "Head Office";
      }
    }

    // Skip comparing if field was not present in Excel data
    if (excelVal === undefined) continue;
    // Optional field: if WorkerCategory was left blank in Excel, do not treat as modified
    if (f.key === "WorkerCategory" && (!excelVal || String(excelVal).trim() === "")) continue;

    if (
      f.type === "boolean" ||
      f.key.startsWith("Is") ||
      f.key.endsWith("Applicable") ||
      f.key.includes("Active") ||
      f.key === "CentralRuleMinWages"
    ) {
      const b1 = parseBool(excelVal);
      const b2 = parseBool(dbVal);
      if (b1 !== b2) return true;
    } else if (f.type === "number") {
      const n1 = excelVal !== "" && excelVal !== null && !isNaN(Number(excelVal)) ? Number(excelVal) : 0;
      const n2 = dbVal !== "" && dbVal !== null && !isNaN(Number(dbVal)) ? Number(dbVal) : 0;
      if (Math.abs(n1 - n2) > 0.0001) return true;
    } else if (f.type === "date" || f.key.includes("Date")) {
      const d1 = safeDate(excelVal);
      const d2 = safeDate(dbVal);
      const isD1Empty = !d1 || d1 === "1900-01-01";
      const isD2Empty = !d2 || d2 === "1900-01-01";
      if (isD1Empty && isD2Empty) continue;
      if (d1 !== d2) return true;
    } else {
      const s1 = String(excelVal ?? "").trim().toLowerCase();
      const s2 = String(dbVal ?? "").trim().toLowerCase();
      if (s1 !== s2) return true;
    }
  }

  return false;
};

// ─────────────────────────────────────────────────────────────
// MASTER CONFIGURATIONS DEFINITION
// ─────────────────────────────────────────────────────────────
export const MASTER_CONFIGS = {
  // 1. Department Master
  DepartmentMaster: {
    title: "Department Master",
    exportFilenamePrefix: "Departments",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Tbl.F_FirmMaster/${firmId || 0}`,
    saveApi: "DepartmentMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: true,
    fields: [
      { key: "Name", label: "Department Name", required: true, example: "Production" },
      { key: "Ordering", label: "Ordering", type: "number", example: "1" },
      { key: "IsSundayInclude", label: "Sunday Include (YES/NO)", type: "boolean", example: "YES" },
      { key: "IsDirectWages", label: "Direct Wages (YES/NO)", type: "boolean", example: "NO" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Core Identification",
        fields: [
          { field: "Department Name (Name)", format: "Text (Required)", allowed: "Any department title", rule: "Mandatory unique key. Matches existing department by name to UPDATE, or INSERTS new.", example: "Production" },
          { field: "Ordering", format: "Number", allowed: "Numeric sequence (1, 2, 3...)", rule: "Controls sorting order of department.", example: "1" },
        ]
      },
      {
        category: "⚙️ Flags & Settings",
        fields: [
          { field: "Sunday Include", format: "YES / NO", allowed: "YES, NO", rule: "Whether Sunday counts for attendance/wages calculation.", example: "YES" },
          { field: "Direct Wages", format: "YES / NO", allowed: "YES, NO", rule: "Whether direct wages apply to this department.", example: "NO" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Set to YES to deactivate this department without deleting.", example: "NO" },
        ]
      }
    ],
    toFormData: (data, firmId, branchId) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);
      fd.append("F_BranchMaster", branchId || 0);
      fd.append("Ordering", data.Ordering || 0);
      fd.append("IsSundayInclude", parseBool(data.IsSundayInclude));
      fd.append("IsDirectWages", parseBool(data.IsDirectWages));
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 2. Designation Master
  DesignationMaster: {
    title: "Designation Master",
    exportFilenamePrefix: "Designations",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/DesignationMaster/Tbl.F_FirmMaster/${firmId || 0}`,
    saveApi: "DesignationMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: true,
    foreignLists: [
      { key: "WorkerCategoryList", url: `${API_WEB_URLS.MASTER}/0/token/WorkerCategory/Id/0`, idField: "Id", nameField: "Name" }
    ],
    fields: [
      { key: "Name", label: "Designation Name", required: true, example: "Accountant" },
      { key: "WorkerCategory", label: "Worker Category", required: false, example: "Staff / Worker" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Core Identification",
        fields: [
          { field: "Designation Name (Name)", format: "Text (Required)", allowed: "Any designation title", rule: "Mandatory unique key. Matches existing designation to UPDATE, or INSERTS new.", example: "Accountant" },
          { field: "Worker Category", format: "Text / ID (Optional)", allowed: "Matching Category Name from Worker Category master", rule: "Optional. Maps to WorkerCategory master automatically by name or ID. Defaults if left blank.", example: "Staff" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Set to YES to mark inactive.", example: "NO" },
        ]
      }
    ],
    toFormData: (data, firmId, branchId, foreignMaps) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);
      let catId = data.F_WorkerCategory || 0;
      if (!catId && data.WorkerCategory && foreignMaps?.WorkerCategoryList) {
        const found = foreignMaps.WorkerCategoryList.find(
          (x) => String(x.Name).trim().toLowerCase() === String(data.WorkerCategory).trim().toLowerCase() || String(x.Id) === String(data.WorkerCategory).trim()
        );
        if (found) catId = found.Id;
      }
      if (!catId && data.existingRecord?.F_WorkerCategory) {
        catId = data.existingRecord.F_WorkerCategory;
      }
      if (!catId && foreignMaps?.WorkerCategoryList?.[0]) {
        catId = foreignMaps.WorkerCategoryList[0].Id;
      }
      fd.append("F_WorkerCategory", catId || 1);
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 3. Firm Master
  FirmMaster: {
    title: "Firm Master",
    exportFilenamePrefix: "Firms",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`,
    saveApi: "FirmMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    foreignLists: [
      { key: "FirmTypeList", url: `${API_WEB_URLS.MASTER}/0/token/FirmTypeMaster/Id/0`, idField: "Id", nameField: "Name" },
      { key: "ConcernTypeList", url: `${API_WEB_URLS.MASTER}/0/token/ConcernTypeMaster/Id/0`, idField: "Id", nameField: "Name" },
      { key: "PremisesTypeList", url: `${API_WEB_URLS.MASTER}/0/token/PremisesTypeMaster/Id/0`, idField: "Id", nameField: "Name" }
    ],
    fields: [
      { key: "Name", label: "Firm Name", required: true, example: "Shine Well Tech" },
      { key: "FirmType", label: "Firm Type", example: "Ordinary" },
      { key: "BranchName", label: "Branch Name", required: true, example: "Head Office" },
      { key: "IsMainBranch", label: "Main Branch (YES/NO)", type: "boolean", example: "YES" },
      { key: "PFNo", label: "PF Number", example: "DL/CPM/1234567" },
      { key: "ESINo", label: "ESI Number", example: "11000123450000001" },
      { key: "NatureOfBusiness", label: "Nature Of Business", example: "Manufacturing" },
      { key: "Signature", label: "Authorised Signatory", example: "Director" },
      { key: "ManagerName", label: "Manager Name", example: "Rajesh Kumar" },
      { key: "ManagerAddress", label: "Manager Address", example: "123 Industrial Area" },
      { key: "IsContractor", label: "Is Contractor (YES/NO)", type: "boolean", example: "NO" },
      { key: "PrincipleEmployer", label: "Principal Employer", example: "ABC Corp" },
      { key: "PrincipleEmployerAddress", label: "Principal Employer Address", example: "Sector 5" },
      { key: "CoAddress", label: "Company Address", example: "Plot 45, Phase 2" },
      { key: "CoOtherAddress", label: "Company Other Address", example: "" },
      { key: "OfficeAddress", label: "Office Address", example: "Main Road" },
      { key: "OfficeAddressOther", label: "Office Address Other", example: "" },
      { key: "PhoneMobileNo", label: "Phone / Mobile No", example: "9876543210" },
      { key: "MaxWorkers", label: "Max Workers", type: "number", example: "100" },
      { key: "UseOfPower", label: "Use Of Power", example: "Yes" },
      { key: "ConcernType", label: "Concern Type", example: "Manufacturing" },
      { key: "PremisesType", label: "Premises Type", example: "Owned" },
      { key: "LicenceFee", label: "Licence Fee", type: "number", example: "5000" },
      { key: "DifferenceFee", label: "Difference Fee", type: "number", example: "0" },
      { key: "AmendmentFee", label: "Amendment Fee", type: "number", example: "0" },
      { key: "DuplicateLicenceFee", label: "Duplicate Licence Fee", type: "number", example: "0" },
      { key: "Connection", label: "Power Connection", example: "Commercial 50KW" },
      { key: "LINNo", label: "LIN Number", example: "1234567890" },
      { key: "FBRegNo", label: "Factory / Shop Reg No", example: "REG-999" },
      { key: "RegRenDate", label: "Registration Renewal Date (YYYY-MM-DD)", type: "date", example: "2025-04-01" },
      { key: "RegExpiryDate", label: "Registration Expiry Date (YYYY-MM-DD)", type: "date", example: "2030-03-31" },
      { key: "InTime", label: "Default Shift In Time", example: "09:00" },
      { key: "OutTime", label: "Default Shift Out Time", example: "18:00" },
      { key: "CentralRuleMinWages", label: "Central Rule Min Wages (YES/NO)", type: "boolean", example: "NO" },
      { key: "PFUserName", label: "PF User Name", example: "" },
      { key: "ESIUserName", label: "ESI User Name", example: "" },
      { key: "FactoryActUserName", label: "Factory Act User Name", example: "" },
      { key: "EmploymentUserName", label: "Employment User Name", example: "" },
      { key: "LabourUserName", label: "Labour User Name", example: "" },
      { key: "OSHUserName", label: "OSH User Name", example: "" },
      { key: "ShramSuvidhaUserName", label: "Shram Suvidha User Name", example: "" },
      { key: "SSOUserName", label: "SSO User Name", example: "" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
      { key: "Remark", label: "Remark", example: "" },
    ],
    guidelines: [
      {
        category: "🔑 Identification & Matching",
        fields: [
          { field: "Firm Name (Name)", format: "Text (Required)", allowed: "Unique Business Name", rule: "PRIMARY KEY. If found in database → UPDATES firm. If not found → INSERTS new firm.", example: "Shine Well Tech Pvt Ltd" },
          { field: "Firm Type", format: "Text / ID", allowed: "Matching Firm Type Name", rule: "Automatically maps to FirmTypeMaster.", example: "Ordinary" },
          { field: "Main Branch Name", format: "Text", allowed: "Branch Name (defaults to 'Head Office')", rule: "Creates default main branch automatically when firm is created.", example: "Head Office" },
        ]
      },
      {
        category: "🏢 Statutory & Address Details",
        fields: [
          { field: "PF Number / ESI Number", format: "Text", allowed: "Statutory IDs", rule: "Statutory codes for employer filing.", example: "DL/CPM/1234567" },
          { field: "Company & Office Address", format: "Text", allowed: "Full Address", rule: "Used in wages slip and registers.", example: "Plot 45, Phase 2" },
          { field: "Date Fields", format: "YYYY-MM-DD", allowed: "Valid ISO Date", rule: "RegRenDate and RegExpiryDate format.", example: "2025-04-01" },
        ]
      }
    ],
    toFormData: (data, firmId, branchId, foreignMaps, existingRecord, customBranchRows) => {
      const fd = new FormData();
      const num = (v) => (v !== null && v !== undefined && v !== "" && !isNaN(Number(v)) ? Number(v) : 0);
      const str = (v) => (v !== null && v !== undefined ? String(v).trim() : "");
      
      fd.append("Name", str(data.Name));
      
      // Resolve FirmType
      let firmTypeId = data.F_FirmType || 0;
      if (!firmTypeId && data.FirmType && foreignMaps?.FirmTypeList) {
        const found = foreignMaps.FirmTypeList.find(
          (x) => String(x.Name).trim().toLowerCase() === String(data.FirmType).trim().toLowerCase() || String(x.Id) === String(data.FirmType).trim()
        );
        if (found) firmTypeId = found.Id;
      }
      if (!firmTypeId && foreignMaps?.FirmTypeList?.[0]) firmTypeId = foreignMaps.FirmTypeList[0].Id;
      fd.append("F_FirmType", firmTypeId || 1);

      // Resolve ConcernType
      let concernTypeId = data.F_ConcernType || 0;
      if (!concernTypeId && data.ConcernType && foreignMaps?.ConcernTypeList) {
        const found = foreignMaps.ConcernTypeList.find(
          (x) => String(x.Name).trim().toLowerCase() === String(data.ConcernType).trim().toLowerCase() || String(x.Id) === String(data.ConcernType).trim()
        );
        if (found) concernTypeId = found.Id;
      }
      fd.append("F_ConcernType", concernTypeId || 0);

      // Resolve PremisesType
      let premisesTypeId = data.F_PremisesType || 0;
      if (!premisesTypeId && data.PremisesType && foreignMaps?.PremisesTypeList) {
        const found = foreignMaps.PremisesTypeList.find(
          (x) => String(x.Name).trim().toLowerCase() === String(data.PremisesType).trim().toLowerCase() || String(x.Id) === String(data.PremisesType).trim()
        );
        if (found) premisesTypeId = found.Id;
      }
      fd.append("F_PremisesType", premisesTypeId || 0);

      fd.append("PFNo", str(data.PFNo));
      fd.append("ESINo", str(data.ESINo));
      fd.append("NatureOfBusiness", str(data.NatureOfBusiness));
      fd.append("Signature", str(data.Signature));
      fd.append("PFUserName", str(data.PFUserName));
      fd.append("PFPassword", str(data.PFPassword));
      fd.append("ESIUserName", str(data.ESIUserName));
      fd.append("ESIPassword", str(data.ESIPassword));
      fd.append("FactoryActUserName", str(data.FactoryActUserName));
      fd.append("FactoryActPassword", str(data.FactoryActPassword));
      fd.append("EmploymentUserName", str(data.EmploymentUserName));
      fd.append("EmploymentPassword", str(data.EmploymentPassword));
      fd.append("LabourUserName", str(data.LabourUserName));
      fd.append("LabourPassword", str(data.LabourPassword));
      fd.append("OSHUserName", str(data.OSHUserName));
      fd.append("OSHPassword", str(data.OSHPassword));
      fd.append("ShramSuvidhaUserName", str(data.ShramSuvidhaUserName));
      fd.append("ShramSuvidhaPassword", str(data.ShramSuvidhaPassword));
      fd.append("SSOUserName", str(data.SSOUserName));
      fd.append("SSOPassword", str(data.SSOPassword));
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));

      fd.append("ManagerName", str(data.ManagerName));
      fd.append("ManagerAddress", str(data.ManagerAddress));
      fd.append("IsContractor", parseBool(data.IsContractor));
      fd.append("PrincipleEmployer", str(data.PrincipleEmployer));
      fd.append("PrincipleEmployerAddress", str(data.PrincipleEmployerAddress));

      fd.append("CoAddress", str(data.CoAddress));
      fd.append("CoOtherAddress", str(data.CoOtherAddress));
      fd.append("OfficeAddress", str(data.OfficeAddress));
      fd.append("OfficeAddressOther", str(data.OfficeAddressOther));
      fd.append("PhoneMobileNo", str(data.PhoneMobileNo));

      fd.append("MaxWorkers", num(data.MaxWorkers));
      fd.append("UseOfPower", str(data.UseOfPower));
      fd.append("LicenceFee", num(data.LicenceFee));
      fd.append("DifferenceFee", num(data.DifferenceFee));
      fd.append("AmendmentFee", num(data.AmendmentFee));
      fd.append("DuplicateLicenceFee", num(data.DuplicateLicenceFee));
      fd.append("Connection", str(data.Connection));
      fd.append("LINNo", str(data.LINNo));
      fd.append("FBRegNo", str(data.FBRegNo));
      if (data.RegRenDate && safeDate(data.RegRenDate)) {
        fd.append("RegRenDate", safeDate(data.RegRenDate));
      }
      if (data.RegExpiryDate && safeDate(data.RegExpiryDate)) {
        fd.append("RegExpiryDate", safeDate(data.RegExpiryDate));
      }

      fd.append("InTime", str(data.InTime));
      fd.append("OutTime", str(data.OutTime));
      fd.append("CentralRuleMinWages", parseBool(data.CentralRuleMinWages));
      fd.append("Remark", str(data.Remark));

      fd.append("FirmNameHindi", str(data.FirmNameHindi));
      fd.append("FirmAddressHindi", str(data.FirmAddressHindi));

      fd.append("CalenderFromDay", str(data.CalenderFromDay));
      fd.append("CalenderFromMonth", str(data.CalenderFromMonth));
      fd.append("CalenderToDay", str(data.CalenderToDay));
      fd.append("CalenderToMonth", str(data.CalenderToMonth));

      // Extract existing main branch ID if updating an existing firm
      let existingBranchId = 0;
      if (existingRecord?.BranchData) {
        try {
          const bList = typeof existingRecord.BranchData === "string" ? JSON.parse(existingRecord.BranchData) : existingRecord.BranchData;
          if (Array.isArray(bList) && bList.length > 0 && bList[0]?.Id) {
            existingBranchId = Number(bList[0].Id);
          }
        } catch (_) {}
      }

      const branchName = str(data.BranchName) || "Head Office";
      const branchRow = {
        Id: Number(data.BranchId || existingBranchId || 0),
        Name: branchName,
        CoAddress: str(data.CoAddress),
        CoOtherAddress: str(data.CoOtherAddress),
        OfficeAddress: str(data.OfficeAddress),
        OfficeAddressOther: str(data.OfficeAddressOther),
        PhoneMobileNo: str(data.PhoneMobileNo),
        IsMainBranch: 1,
        CentralRuleMinWages: parseBool(data.CentralRuleMinWages) ? 1 : 0,
        PFNo: str(data.PFNo),
        ESINo: str(data.ESINo),
        LINNo: str(data.LINNo),
        FBRegNo: str(data.FBRegNo),
        RegRenDate: safeDate(data.RegRenDate) || "1900-01-01",
        RegExpiryDate: safeDate(data.RegExpiryDate) || "1900-01-01",
        Connection: str(data.Connection),
        FirmNameHindi: str(data.FirmNameHindi),
        FirmAddressHindi: str(data.FirmAddressHindi),
        ManagerName: str(data.ManagerName),
        ManagerAddress: str(data.ManagerAddress),
        IsContractor: parseBool(data.IsContractor) ? 1 : 0,
        PrincipleEmployer: str(data.PrincipleEmployer),
        PrincipleEmployerAddress: str(data.PrincipleEmployerAddress),
        PFUserName: str(data.PFUserName),
        PFPassword: str(data.PFPassword),
        ESIUserName: str(data.ESIUserName),
        ESIPassword: str(data.ESIPassword),
        FactoryActUserName: str(data.FactoryActUserName),
        FactoryActPassword: str(data.FactoryActPassword),
        EmploymentUserName: str(data.EmploymentUserName),
        EmploymentPassword: str(data.EmploymentPassword),
        LabourUserName: str(data.LabourUserName),
        LabourPassword: str(data.LabourPassword),
        OSHUserName: str(data.OSHUserName),
        OSHPassword: str(data.OSHPassword),
        ShramSuvidhaUserName: str(data.ShramSuvidhaUserName),
        ShramSuvidhaPassword: str(data.ShramSuvidhaPassword),
        SSOUserName: str(data.SSOUserName),
        SSOPassword: str(data.SSOPassword),
      };

      fd.append("BranchJson", JSON.stringify(customBranchRows && customBranchRows.length > 0 ? customBranchRows : [branchRow]));
      fd.append("ShiftTimeJson", "[]");

      return fd;
    }
  },

  // 4. Firm Type Master
  FirmTypeMaster: {
    title: "Firm Type Master",
    exportFilenamePrefix: "FirmTypes",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/FirmTypeMaster/Id/0`,
    saveApi: "FirmTypeMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Firm Type Name", required: true, example: "Private Limited" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Firm Type Name (Name)", format: "Text (Required)", allowed: "Proprietorship, Partnership, Private Limited, etc.", rule: "Mandatory name. Updates if exists, else Inserts.", example: "Private Limited" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates type if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 5. Concern Type Master
  ConcernTypeMaster: {
    title: "Concern Type Master",
    exportFilenamePrefix: "ConcernTypes",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/ConcernTypeMaster/Id/0`,
    saveApi: "ConcernTypeMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Concern Type Name", required: true, example: "Manufacturing" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Concern Type Name (Name)", format: "Text (Required)", allowed: "Any business concern classification", rule: "Updates if exists, else Inserts.", example: "Manufacturing" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates concern type if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 6. Premises Type Master
  PremisesTypeMaster: {
    title: "Premises Type Master",
    exportFilenamePrefix: "PremisesTypes",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/PremisesTypeMaster/Id/0`,
    saveApi: "PremisesTypeMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Premises Type Name", required: true, example: "Owned" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Premises Type Name (Name)", format: "Text (Required)", allowed: "Owned, Rented, Leased, etc.", rule: "Updates if exists, else Inserts.", example: "Owned" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates premises type if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 7. Worker Category
  WorkerCategory: {
    title: "Worker Category Master",
    exportFilenamePrefix: "WorkerCategories",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/WorkerCategory/Id/0`,
    saveApi: "WorkerCategory/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Worker Category Name", required: true, example: "Skilled" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Worker Category Name (Name)", format: "Text (Required)", allowed: "Skilled, Semi-Skilled, Unskilled, Staff, etc.", rule: "Updates if exists, else Inserts.", example: "Skilled" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates category if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 8. Employee Type Master
  EmployeeTypeMaster: {
    title: "Employee Type Master",
    exportFilenamePrefix: "EmployeeTypes",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/EmployeeTypeMaster/Id/0`,
    saveApi: "EmployeeTypeMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Employee Type Name", required: true, example: "Permanent" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Employee Type Name (Name)", format: "Text (Required)", allowed: "Permanent, Temporary, Trainee, Contractual, etc.", rule: "Updates if exists, else Inserts.", example: "Permanent" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates type if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 8b. Reason to Left Master
  ReasonOfLeftMaster: {
    title: "Reason To Left Master",
    exportFilenamePrefix: "ReasonsToLeft",
    listApi: () => `${API_WEB_URLS.MASTER}/0/token/ReasonOfLeftMaster/Id/0`,
    saveApi: "ReasonOfLeftMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: false,
    fields: [
      { key: "Name", label: "Reason to Left Name", required: true, example: "Resignation" },
      { key: "IsNotInUse", label: "Not In Use (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Details",
        fields: [
          { field: "Reason to Left Name (Name)", format: "Text (Required)", allowed: "Resignation, Retirement, Termination, Better Prospects, etc.", rule: "Updates if exists, else Inserts.", example: "Resignation" },
          { field: "Not In Use", format: "YES / NO", allowed: "YES, NO", rule: "Deactivates reason if YES.", example: "NO" },
        ]
      }
    ],
    toFormData: (data) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("IsNotInUse", parseBool(data.IsNotInUse));
      return fd;
    }
  },

  // 9. Leave Master
  LeaveMaster: {
    title: "Leave Master",
    exportFilenamePrefix: "Leaves",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${firmId || 0}`,
    saveApi: "LeaveMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: true,
    foreignLists: [
      { key: "YearList", url: `${API_WEB_URLS.MASTER}/0/token/YearMaster/Id/0`, idField: "Id", nameField: "Name" }
    ],
    fields: [
      { key: "Name", label: "Leave Name", required: true, example: "Earned Leave (EL)" },
      { key: "Year", label: "Year", example: "2025" },
      { key: "IsYearCarryforward", label: "Year Carry Forward (YES/NO)", type: "boolean", example: "YES" },
      { key: "MaxLimit", label: "Max Limit", type: "number", example: "15" },
      { key: "LeavePayableLimit", label: "Leave Payable Limit", type: "number", example: "15" },
      { key: "MaxContinousLeave", label: "Max Continuous Leave", type: "number", example: "3" },
      { key: "DaysToCalculateLeaveWithWages", label: "Days To Calc Leave With Wages", type: "number", example: "20" },
      { key: "CalenderFromMonth", label: "From Month (01-12)", example: "01" },
      { key: "CalenderFromDay", label: "From Day (01-31)", example: "01" },
      { key: "CalenderToMonth", label: "To Month (01-12)", example: "12" },
      { key: "CalenderToDay", label: "To Day (01-31)", example: "31" },
    ],
    guidelines: [
      {
        category: "🔑 Leave Details",
        fields: [
          { field: "Leave Name (Name)", format: "Text (Required)", allowed: "CL, SL, EL, Casual Leave, etc.", rule: "Matches leave name in firm to UPDATE or INSERTS new.", example: "Casual Leave" },
          { field: "Year", format: "Number / Text", allowed: "Calendar/Financial Year", rule: "Maps to YearMaster or defaults to active year.", example: "2025" },
          { field: "Carry Forward", format: "YES / NO", allowed: "YES, NO", rule: "Whether remaining leaves carry over to next year.", example: "YES" },
        ]
      },
      {
        category: "⚙️ Limits & Rules",
        fields: [
          { field: "Max Limit", format: "Number", allowed: "Total allowed leaves per year", rule: "Maximum leave balance allowed.", example: "12" },
          { field: "Leave Payable Limit", format: "Number", allowed: "Encashable / Payable limit", rule: "Maximum leaves that can be encashed.", example: "12" },
          { field: "Max Continuous Leave", format: "Number", allowed: "Continuous days limit", rule: "Maximum days taken at one stretch.", example: "3" },
        ]
      },
      {
        category: "📅 Calendar Period",
        fields: [
          { field: "From Month / Day", format: "Month (01-12), Day (01-31)", allowed: "01 to 12 / 01 to 31", rule: "Start of leave calendar period.", example: "01" },
          { field: "To Month / Day", format: "Month (01-12), Day (01-31)", allowed: "01 to 12 / 01 to 31", rule: "End of leave calendar period.", example: "12" },
        ]
      }
    ],
    toFormData: (data, firmId, branchId, foreignMaps) => {
      const fd = new FormData();
      const num = (v) => (v !== null && v !== undefined && v !== "" && !isNaN(Number(v)) ? Number(v) : 0);
      fd.append("Name", String(data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);

      let yearId = data.F_YearMaster || 0;
      if (!yearId && data.Year && foreignMaps?.YearList) {
        const found = foreignMaps.YearList.find(
          (x) => String(x.Name).trim().toLowerCase() === String(data.Year).trim().toLowerCase() || String(x.Id) === String(data.Year).trim()
        );
        if (found) yearId = found.Id;
      }
      if (!yearId && foreignMaps?.YearList?.[0]) yearId = foreignMaps.YearList[0].Id;
      fd.append("F_YearMaster", yearId || 1);

      fd.append("IsYearCarryforward", parseBool(data.IsYearCarryforward));
      fd.append("MaxLimit", num(data.MaxLimit));
      fd.append("LeavePayableLimit", num(data.LeavePayableLimit));
      fd.append("MaxContinousLeave", num(data.MaxContinousLeave));
      fd.append("DaysToCalculateLeaveWithWages", num(data.DaysToCalculateLeaveWithWages));
      fd.append("CalenderFromMonth", data.CalenderFromMonth ? String(data.CalenderFromMonth).padStart(2, "0") : "");
      fd.append("CalenderFromDay", data.CalenderFromDay ? String(data.CalenderFromDay).padStart(2, "0") : "");
      fd.append("CalenderToMonth", data.CalenderToMonth ? String(data.CalenderToMonth).padStart(2, "0") : "");
      fd.append("CalenderToDay", data.CalenderToDay ? String(data.CalenderToDay).padStart(2, "0") : "");
      return fd;
    }
  },

  // 10. Allowance Master
  AllowanceMaster: {
    title: "Allowance Master",
    exportFilenamePrefix: "Allowances",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/TBL.F_FirmMaster/${firmId || 0}`,
    saveApi: "AllowanceMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: true,
    fields: [
      { key: "Name", label: "Allowance Name", required: true, example: "Dearness Allowance (DA)" },
      { key: "Ordering", label: "Ordering", type: "number", example: "1" },
      { key: "IsActive", label: "Active (YES/NO)", type: "boolean", example: "YES" },
      { key: "IsApplicable", label: "Applicable (YES/NO)", type: "boolean", example: "YES" },
    ],
    guidelines: [
      {
        category: "🔑 Allowance Details",
        fields: [
          { field: "Allowance Name (Name)", format: "Text (Required)", allowed: "DA, HRA, Conveyance, Special Allowance, etc.", rule: "Matches allowance name in firm to UPDATE or INSERTS new.", example: "HRA" },
          { field: "Ordering", format: "Number", allowed: "Display order sequence (1, 2, 3...)", rule: "Ordering in wages slips and sheets.", example: "1" },
          { field: "Active & Applicable", format: "YES / NO", allowed: "YES, NO", rule: "Controls whether the allowance is active in payroll processing.", example: "YES" },
        ]
      }
    ],
    toFormData: (data, firmId) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);
      fd.append("Ordering", data.Ordering || 1);
      fd.append("IsActive", data.IsActive !== undefined ? parseBool(data.IsActive) : true);
      fd.append("IsApplicable", parseBool(data.IsApplicable));
      return fd;
    }
  },

  // 11. Deduction Master
  DeductionMaster: {
    title: "Deduction Master",
    exportFilenamePrefix: "Deductions",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/TBL.F_FirmMaster/${firmId || 0}`,
    saveApi: "DeductionMaster/0/token",
    matchKey: "Name",
    isFirmSpecific: true,
    fields: [
      { key: "Name", label: "Deduction Name", required: true, example: "TDS / Advance" },
      { key: "Ordering", label: "Ordering", type: "number", example: "1" },
      { key: "IsActive", label: "Active (YES/NO)", type: "boolean", example: "YES" },
      { key: "IsApplicable", label: "Applicable (YES/NO)", type: "boolean", example: "YES" },
    ],
    guidelines: [
      {
        category: "🔑 Deduction Details",
        fields: [
          { field: "Deduction Name (Name)", format: "Text (Required)", allowed: "Advance, Loan, Uniform, Welfare, TDS, etc.", rule: "Matches deduction name in firm to UPDATE or INSERTS new.", example: "Salary Advance" },
          { field: "Ordering", format: "Number", allowed: "Display order sequence", rule: "Ordering in wages registers.", example: "1" },
          { field: "Active & Applicable", format: "YES / NO", allowed: "YES, NO", rule: "Controls whether the deduction is active in payroll processing.", example: "YES" },
        ]
      }
    ],
    toFormData: (data, firmId) => {
      const fd = new FormData();
      fd.append("Name", String(data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);
      fd.append("Ordering", data.Ordering || 1);
      fd.append("IsActive", data.IsActive !== undefined ? parseBool(data.IsActive) : true);
      fd.append("IsApplicable", parseBool(data.IsApplicable));
      return fd;
    }
  },

  // 12. Extra Pay Master
  ExtraPayMaster: {
    title: "Extra Pay Master",
    exportFilenamePrefix: "ExtraPays",
    listApi: (firmId) => `${API_WEB_URLS.MASTER}/0/token/ExtraPayMaster/TBL.F_FirmMaster/${firmId || 0}`,
    saveApi: "ExtraPayMaster/0/token",
    matchKey: "ExtraPayName",
    isFirmSpecific: true,
    fields: [
      { key: "ExtraPayName", label: "Extra Pay Name", required: true, example: "Overtime Extra Pay" },
      { key: "Ordering", label: "Ordering", type: "number", example: "1" },
      { key: "IsActive", label: "Active (YES/NO)", type: "boolean", example: "YES" },
      { key: "PFOnExtraPay", label: "PF On Extra Pay (YES/NO)", type: "boolean", example: "NO" },
      { key: "ESIOnExtraPay", label: "ESI On Extra Pay (YES/NO)", type: "boolean", example: "NO" },
    ],
    guidelines: [
      {
        category: "🔑 Extra Pay Details",
        fields: [
          { field: "Extra Pay Name (ExtraPayName)", format: "Text (Required)", allowed: "Overtime, Incentive, Holiday Pay, Attendance Bonus, etc.", rule: "Matches extra pay name in firm to UPDATE or INSERTS new.", example: "Production Incentive" },
          { field: "Ordering", format: "Number", allowed: "Numeric sequence", rule: "Display ordering sequence.", example: "1" },
          { field: "PF / ESI On Extra Pay", format: "YES / NO", allowed: "YES, NO", rule: "Controls whether PF or ESI deductions apply to this extra payment.", example: "NO" },
        ]
      }
    ],
    toFormData: (data, firmId) => {
      const fd = new FormData();
      fd.append("ExtraPayName", String(data.ExtraPayName || data.Name || "").trim());
      fd.append("F_FirmMaster", firmId || 0);
      fd.append("Ordering", data.Ordering || 1);
      fd.append("IsActive", data.IsActive !== undefined ? parseBool(data.IsActive) : true);
      fd.append("PFOnExtraPay", parseBool(data.PFOnExtraPay));
      fd.append("ESIOnExtraPay", parseBool(data.ESIOnExtraPay));
      return fd;
    }
  },
};

// ─────────────────────────────────────────────────────────────
// REUSABLE MASTER IMPORT EXPORT COMPONENT
// ─────────────────────────────────────────────────────────────
export const MasterImportExport = ({ masterKey }) => {
  const config = MASTER_CONFIGS[masterKey];
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { firm: selectedFirm, firmId: sessionFirmId, branchId: sessionBranchId } = useSession();
  const firmId = sessionFirmId ? String(sessionFirmId) : "0";
  const branchId = sessionBranchId ? String(sessionBranchId) : "0";

  // Auth token
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  })();
  const authToken = authUser?.token || API_WEB_URLS.Token;
  const userId = authUser?.Id ? String(authUser.Id) : "1";

  // State
  const [existingList, setExistingList] = useState([]);
  const [foreignMaps, setForeignMaps] = useState({});
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Import State
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [importResults, setImportResults] = useState({
    total: 0,
    success: 0,
    inserted: 0,
    updated: 0,
    failed: 0,
    errors: [],
    details: [],
  });

  // Guidelines Modal State
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [guidelineSearch, setGuidelineSearch] = useState("");

  const fileInputRef = useRef(null);

  // Load existing records + foreign lists directly via fetch
  const loadExistingData = useCallback(async () => {
    if (!config) return;
    setLoadingSummary(true);
    try {
      let records = [];

      // 1. Fetch directly from listApi (100% reliable REST endpoint)
      const listEndpoint = typeof config.listApi === "function" ? config.listApi(firmId) : config.listApi;
      const listUrl = listEndpoint.startsWith("http")
        ? listEndpoint
        : `${API_WEB_URLS.BASE}${listEndpoint}`;

      const res = await fetch(listUrl, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });

      if (res.ok) {
        const json = await res.json();
        records = json?.data?.dataList || json?.data?.rows || json?.data || [];
        if (!Array.isArray(records) && typeof records === "object") {
          records = Object.values(records);
        }
      }

      setExistingList(Array.isArray(records) ? records : []);

      // 2. Load foreign lists if any
      if (config.foreignLists && config.foreignLists.length > 0) {
        const maps = {};
        for (const fl of config.foreignLists) {
          const flUrl = fl.url.startsWith("http") ? fl.url : `${API_WEB_URLS.BASE}${fl.url}`;
          const flRes = await fetch(flUrl, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          }).catch(() => null);
          if (flRes && flRes.ok) {
            const flJson = await flRes.json();
            const flList = flJson?.data?.dataList || flJson?.data?.rows || flJson?.data || [];
            maps[fl.key] = Array.isArray(flList) ? flList : [];
          }
        }
        setForeignMaps(maps);
      }
    } catch (e) {
      console.error(`Failed to load data for ${config.title}:`, e);
      setExistingList([]);
    } finally {
      setLoadingSummary(false);
    }
  }, [config, firmId, authToken]);

  useEffect(() => {
    loadExistingData();
  }, [loadExistingData]);

  if (!config) {
    return (
      <div className="p-4 text-center">
        <h4>Invalid Master Configuration for: {masterKey}</h4>
        <button className="btn btn-secondary mt-3" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // Export Master Data to Excel
  // ──────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      let records = existingList;
      if (!records || records.length === 0) {
        const listEndpoint = typeof config.listApi === "function" ? config.listApi(firmId) : config.listApi;
        const listUrl = listEndpoint.startsWith("http") ? listEndpoint : `${API_WEB_URLS.BASE}${listEndpoint}`;
        try {
          const res = await fetch(listUrl, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          if (res.ok) {
            const json = await res.json();
            const list = json?.data?.dataList || json?.data?.rows || json?.data || [];
            if (Array.isArray(list)) {
              records = list;
              setExistingList(list);
            }
          }
        } catch (e) {
          console.warn("Export listApi fetch failed:", e);
        }
      }

      if (!records || records.length === 0) {
        toast.warning(`No ${config.title} records found to export.`);
        setExporting(false);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const sheet = workbook.addWorksheet(config.exportFilenamePrefix || "Data", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Define columns: always include ID as first column for accurate update tracking
      sheet.columns = [
        { header: "ID", key: "Id", width: 12 },
        ...config.fields.filter((f) => f.key !== "Id").map((f) => ({
          header: f.label,
          key: f.key,
          width: Math.max(f.label.length + 5, 20),
        })),
      ];

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 32;

      // Add data rows
      let globalRowIdx = 0;
      records.forEach((rec, idx) => {
        if (masterKey === "FirmMaster") {
          let branches = [];
          if (rec.BranchData) {
            try {
              const parsed = typeof rec.BranchData === "string" ? JSON.parse(rec.BranchData) : rec.BranchData;
              if (Array.isArray(parsed) && parsed.length > 0) {
                branches = parsed;
              }
            } catch (_) {}
          }
          if (branches.length === 0) {
            branches = [{
              Name: rec.BranchName || "Head Office",
              IsMainBranch: 1,
              CoAddress: rec.CoAddress,
              CoOtherAddress: rec.CoOtherAddress,
              OfficeAddress: rec.OfficeAddress,
              OfficeAddressOther: rec.OfficeAddressOther,
              PhoneMobileNo: rec.PhoneMobileNo,
              PFNo: rec.PFNo,
              ESINo: rec.ESINo,
              LINNo: rec.LINNo,
              FBRegNo: rec.FBRegNo,
              RegRenDate: rec.RegRenDate,
              RegExpiryDate: rec.RegExpiryDate,
              Connection: rec.Connection,
              ManagerName: rec.ManagerName,
              ManagerAddress: rec.ManagerAddress,
              IsContractor: rec.IsContractor,
              PrincipleEmployer: rec.PrincipleEmployer,
              PrincipleEmployerAddress: rec.PrincipleEmployerAddress,
              CentralRuleMinWages: rec.CentralRuleMinWages,
              PFUserName: rec.PFUserName,
              ESIUserName: rec.ESIUserName,
              FactoryActUserName: rec.FactoryActUserName,
              EmploymentUserName: rec.EmploymentUserName,
              LabourUserName: rec.LabourUserName,
            }];
          }

          branches.forEach((b) => {
            const rowData = {
              Id: rec.Id ?? "",
            };
            config.fields.filter((f) => f.key !== "Id").forEach((f) => {
              let val = undefined;
              if (f.key === "BranchName") val = b.Name || "Head Office";
              else if (f.key === "IsMainBranch") val = fmtBool(b.IsMainBranch);
              else if (b[f.key] !== undefined && b[f.key] !== null && b[f.key] !== "") val = b[f.key];
              else val = rec[f.key];

              if (f.key === "FirmType" && (val === undefined || val === null)) val = rec.FirmTypeName || rec.FirmType;
              if (f.key === "ConcernType" && (val === undefined || val === null)) val = rec.ConcernTypeName || rec.ConcernType;
              if (f.key === "PremisesType" && (val === undefined || val === null)) val = rec.PremisesTypeName || rec.PremisesType;

              if (f.type === "boolean" || f.key.startsWith("Is") || f.key.endsWith("Applicable") || f.key.includes("Active") || f.key === "CentralRuleMinWages") {
                val = fmtBool(val);
              } else if (f.type === "date" || f.key.includes("Date")) {
                val = safeDate(val);
              }
              rowData[f.key] = val ?? "";
            });

            const row = sheet.addRow(rowData);
            const bgColor = globalRowIdx % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB";
            globalRowIdx++;
            row.eachCell((cell) => {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
              cell.alignment = { vertical: "middle", horizontal: "left" };
              cell.border = {
                top: { style: "thin", color: { argb: "FFE5E7EB" } },
                left: { style: "thin", color: { argb: "FFE5E7EB" } },
                bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
                right: { style: "thin", color: { argb: "FFE5E7EB" } },
              };
            });
            row.height = 22;
          });
        } else {
          const rowData = {
            Id: rec.Id ?? "",
          };
          config.fields.filter((f) => f.key !== "Id").forEach((f) => {
            let val = rec[f.key];
            // Check for alternative name mappings in record
            if (val === undefined || val === null || val === "") {
              if (f.key === "ExtraPayName" && rec.Name !== undefined) val = rec.Name;
              if (f.key === "Name" && rec.ExtraPayName !== undefined) val = rec.ExtraPayName;
              if (f.key === "WorkerCategory" && (rec.WorkerCategoryName || rec.WorkerCategory_Name || rec.F_WorkerCategoryName)) {
                val = rec.WorkerCategoryName || rec.WorkerCategory_Name || rec.F_WorkerCategoryName;
              }
              if (f.key === "FirmType" && (rec.FirmTypeName || rec.FirmType_Name)) val = rec.FirmTypeName || rec.FirmType_Name;
              if (f.key === "ConcernType" && (rec.ConcernTypeName || rec.ConcernType_Name)) val = rec.ConcernTypeName || rec.ConcernType_Name;
              if (f.key === "PremisesType" && (rec.PremisesTypeName || rec.PremisesType_Name)) val = rec.PremisesTypeName || rec.PremisesType_Name;
              if (f.key === "Year" && (rec.YearName || rec.Year_Name)) val = rec.YearName || rec.Year_Name;
            }

            // Foreign Key Name Lookup fallback via foreignMaps if val is missing or numeric ID
            if (f.key === "WorkerCategory" && (!val || typeof val === "number" || !isNaN(Number(val)))) {
              const catId = rec.F_WorkerCategory ?? rec.WorkerCategory ?? rec.WorkerCategoryId ?? val;
              if (catId && foreignMaps?.WorkerCategoryList) {
                const found = foreignMaps.WorkerCategoryList.find(
                  (x) => String(x.Id) === String(catId) || String(x.Name).trim().toLowerCase() === String(catId).trim().toLowerCase()
                );
                if (found) val = found.Name;
              }
            }
            if (f.key === "FirmType" && (!val || typeof val === "number" || !isNaN(Number(val)))) {
              const ftId = rec.F_FirmType ?? rec.FirmType ?? val;
              if (ftId && foreignMaps?.FirmTypeList) {
                const found = foreignMaps.FirmTypeList.find(
                  (x) => String(x.Id) === String(ftId) || String(x.Name).trim().toLowerCase() === String(ftId).trim().toLowerCase()
                );
                if (found) val = found.Name;
              }
            }
            if (f.key === "ConcernType" && (!val || typeof val === "number" || !isNaN(Number(val)))) {
              const ctId = rec.F_ConcernType ?? rec.ConcernType ?? val;
              if (ctId && foreignMaps?.ConcernTypeList) {
                const found = foreignMaps.ConcernTypeList.find(
                  (x) => String(x.Id) === String(ctId) || String(x.Name).trim().toLowerCase() === String(ctId).trim().toLowerCase()
                );
                if (found) val = found.Name;
              }
            }
            if (f.key === "PremisesType" && (!val || typeof val === "number" || !isNaN(Number(val)))) {
              const ptId = rec.F_PremisesType ?? rec.PremisesType ?? val;
              if (ptId && foreignMaps?.PremisesTypeList) {
                const found = foreignMaps.PremisesTypeList.find(
                  (x) => String(x.Id) === String(ptId) || String(x.Name).trim().toLowerCase() === String(ptId).trim().toLowerCase()
                );
                if (found) val = found.Name;
              }
            }
            if (f.key === "Year" && (!val || typeof val === "number" || !isNaN(Number(val)))) {
              const yrId = rec.F_YearMaster ?? rec.Year ?? val;
              if (yrId && foreignMaps?.YearList) {
                const found = foreignMaps.YearList.find(
                  (x) => String(x.Id) === String(yrId) || String(x.Name).trim().toLowerCase() === String(yrId).trim().toLowerCase()
                );
                if (found) val = found.Name;
              }
            }

            if (f.type === "boolean" || f.key.startsWith("Is") || f.key.endsWith("Applicable") || f.key.includes("Active") || f.key === "CentralRuleMinWages") {
              val = fmtBool(val);
            } else if (f.type === "date" || f.key.includes("Date")) {
              val = safeDate(val);
            }
            rowData[f.key] = val ?? "";
          });

          const row = sheet.addRow(rowData);
          const bgColor = globalRowIdx % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB";
          globalRowIdx++;
          row.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
            cell.alignment = { vertical: "middle", horizontal: "left" };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE5E7EB" } },
              left: { style: "thin", color: { argb: "FFE5E7EB" } },
              bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
              right: { style: "thin", color: { argb: "FFE5E7EB" } },
            };
          });
          row.height = 22;
        }
      });

      // Auto-fit column widths
      sheet.columns.forEach((col) => {
        let maxLen = col.header ? col.header.length : 12;
        col.eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        col.width = Math.min(Math.max(maxLen + 4, 14), 50);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      const firmTag = config.isFirmSpecific && (selectedFirm?.FirmName || selectedFirm?.Name)
        ? `_${(selectedFirm.FirmName || selectedFirm.Name).replace(/[^a-zA-Z0-9_-]/g, "_")}`
        : "";
      saveAs(blob, `${config.exportFilenamePrefix}_Export${firmTag}_${dateStr}.xlsx`);

      toast.success(`✅ Exported ${records.length} ${config.title} records successfully!`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed. Please check network/API.");
    } finally {
      setExporting(false);
    }
  };

  // ──────────────────────────────────────────
  // Template Download (Headings Only)
  // ──────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const sheet = workbook.addWorksheet(`${config.exportFilenamePrefix} Template`, {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      sheet.columns = [
        { header: "ID (Leave Blank For New)", key: "Id", width: 26 },
        ...config.fields.filter((f) => f.key !== "Id").map((f) => ({
          header: f.label,
          key: f.key,
          width: Math.max(f.label.length + 5, 22),
        })),
      ];

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1D4ED8" }, // Clean Professional Blue
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 32;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${config.exportFilenamePrefix}_Import_Template.xlsx`);
      toast.success("📄 Template downloaded with only column headings (ready for data entry)!");
    } catch (err) {
      console.error("Template download error:", err);
      toast.error("Failed to download template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ──────────────────────────────────────────
  // Parse Excel File on Upload
  // ──────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Invalid file! Please upload an Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }

    setImportFile(file);
    setIsParsing(true);
    setPreviewData([]);
    setShowPreview(false);

    try {
      // Ensure we have the latest existing records loaded from DB
      let currentExisting = existingList;
      if (!currentExisting || currentExisting.length === 0) {
        const listEndpoint = typeof config.listApi === "function" ? config.listApi(firmId) : config.listApi;
        const listUrl = listEndpoint.startsWith("http") ? listEndpoint : `${API_WEB_URLS.BASE}${listEndpoint}`;
        try {
          const res = await fetch(listUrl, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          if (res.ok) {
            const json = await res.json();
            const fetched = json?.data?.dataList || json?.data?.rows || json?.data || [];
            if (Array.isArray(fetched) && fetched.length > 0) {
              currentExisting = fetched;
              setExistingList(fetched);
            }
          }
        } catch (fetchErr) {
          console.warn("Could not fetch fresh list on file parse:", fetchErr);
        }
      }

      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      if (!sheet) {
        toast.error("No sheet found in the Excel file.");
        setIsParsing(false);
        return;
      }

      // Get headers from first row
      const headers = [];
      sheet.getRow(1).eachCell((cell, colNum) => {
        headers[colNum - 1] = String(cell.value || "").trim();
      });

      // Map labels and direct keys to field keys
      const labelToKey = {
        id: "Id",
        "id (leave blank for new)": "Id",
        "record id": "Id",
        code: "Id",
        "firm id": "Id",
        "dept id": "Id",
        "department id": "Id",
        "designation id": "Id",
      };
      config.fields.forEach((f) => {
        labelToKey[f.label.toLowerCase()] = f.key;
        labelToKey[f.key.toLowerCase()] = f.key;
        // Clean variations without parentheses
        const cleanLabel = f.label.replace(/\([^)]*\)/g, "").trim().toLowerCase();
        labelToKey[cleanLabel] = f.key;
      });

      const rows = [];
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row

        const obj = {};
        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
          const headerName = headers[colNum - 1];
          if (!headerName) return;

          const matchedKey = labelToKey[headerName.toLowerCase()] || labelToKey[headerName.replace(/\([^)]*\)/g, "").trim().toLowerCase()] || headerName;
          let val = cell.value;
          if (val && typeof val === "object" && val.result !== undefined) val = val.result;
          if (val instanceof Date) val = safeDate(val);
          const valStr = val !== null && val !== undefined ? String(val).trim() : "";
          obj[matchedKey] = valStr;
        });

        // Skip completely empty rows
        const hasData = Object.values(obj).some((v) => v && String(v).trim() !== "");
        if (!hasData) return;

        // Validation checking
        const errors = [];
        config.fields.forEach((f) => {
          if (f.required) {
            const val = obj[f.key];
            if (!val || String(val).trim() === "") {
              errors.push(`${f.label} is required`);
            }
          }
        });

        // Match existing record in DB:
        // 1. By explicit ID if provided (> 0)
        let existingRecord = null;
        const parsedId = Number(obj.Id || 0);
        if (parsedId > 0 && Array.isArray(currentExisting)) {
          existingRecord = currentExisting.find((r) => Number(r.Id) === parsedId);
        }

        // 2. If no ID match, match by primary name / matchKey (e.g. Department Name, Firm Name)
        if (!existingRecord && Array.isArray(currentExisting)) {
          const matchVal = String(
            obj[config.matchKey] ||
            obj.Name ||
            obj.DepartmentName ||
            obj.DesignationName ||
            obj.FirmName ||
            obj.BankName ||
            obj.ShiftName ||
            obj.ExtraPayName ||
            ""
          ).trim().toLowerCase();

          if (matchVal !== "") {
            existingRecord = currentExisting.find((r) => {
              const rVal = String(
                r[config.matchKey] ||
                r.Name ||
                r.DepartmentName ||
                r.DesignationName ||
                r.FirmName ||
                r.BankName ||
                r.ShiftName ||
                r.ExtraPayName ||
                ""
              ).trim().toLowerCase();
              return rVal === matchVal;
            });
          }
        }

        const isNew = !existingRecord;
        let isModified = false;
        let rowType = "INSERT";

        if (existingRecord) {
          obj.Id = existingRecord.Id; // Ensure DB record ID is bound for update!
          isModified = isRecordModified(obj, existingRecord, config, foreignMaps);
          rowType = isModified ? "UPDATE" : "EXISTING";
        } else {
          rowType = "INSERT";
          isModified = false;
        }

        rows.push({
          sNo: rows.length + 1,
          rowIndex: rowNumber,
          data: obj,
          errors,
          status: errors.length > 0 ? "error" : "pending",
          type: rowType, // "INSERT" | "UPDATE" | "EXISTING"
          isNew: isNew,
          isModified: isModified,
          existingRecord: existingRecord,
          existingId: existingRecord?.Id || 0,
        });
      });

      if (rows.length === 0) {
        toast.warning("No data rows found in the Excel file.");
        setIsParsing(false);
        return;
      }

      setPreviewData(rows);
      setShowPreview(true);
      toast.success(`📊 Parsed ${rows.length} rows. Review the preview and click "Run Import".`);
    } catch (err) {
      console.error("Parse error:", err);
      toast.error("Failed to parse Excel file. Please check file formatting.");
    } finally {
      setIsParsing(false);
    }
  };

  // ──────────────────────────────────────────
  // Run Import (Auto Insert / Update)
  // ──────────────────────────────────────────
  const handleImport = async () => {
    const validRows = previewData.filter((r) => r.status !== "error");
    if (validRows.length === 0) {
      toast.warning("No valid rows to import. Please resolve validation errors.");
      return;
    }

    setImporting(true);
    setImportProgress(10);

    let insertedCount = 0;
    let updatedCount = 0;
    let unchangedCount = 0;
    let failedCount = 0;
    const errors = [];
    const details = [];

    const updatedPreview = [...previewData];
    const totalToProcess = validRows.length;

    try {
      if (masterKey === "FirmMaster") {
        const firmGroups = {};
        validRows.forEach((item) => {
          const fName = String(item.data.Name || "").trim().toLowerCase();
          if (!firmGroups[fName]) firmGroups[fName] = [];
          firmGroups[fName].push(item);
        });

        const groupKeys = Object.keys(firmGroups);
        const totalGroups = groupKeys.length;

        for (let gIdx = 0; gIdx < totalGroups; gIdx++) {
          const fName = groupKeys[gIdx];
          const groupItems = firmGroups[fName];
          const firstItem = groupItems[0];
          const firmData = { ...firstItem.data };
          const existingRecord = firstItem.existingRecord;
          const targetId = firstItem.existingId || (!firstItem.isNew && firmData.Id ? Number(firmData.Id) : 0) || 0;
          const rowMatchName = firmData.Name || `Firm ${fName}`;

          // Parse existing branches of this firm
          let existingBranches = [];
          if (existingRecord?.BranchData) {
            try {
              const bList = typeof existingRecord.BranchData === "string" ? JSON.parse(existingRecord.BranchData) : existingRecord.BranchData;
              if (Array.isArray(bList)) existingBranches = bList;
            } catch (_) {}
          }

          const hasAnyMainSpecified = groupItems.some((x) => parseBool(x.data.IsMainBranch));

          // Build all branch rows
          const branchRows = groupItems.map((item, bIdx) => {
            const bData = item.data;
            const bName = (bData.BranchName ? String(bData.BranchName).trim() : "") || (bIdx === 0 ? "Head Office" : `Branch ${bIdx + 1}`);

            let matchedBranchId = Number(bData.BranchId || 0);
            if (!matchedBranchId && existingBranches.length > 0) {
              const found = existingBranches.find(
                (eb) => String(eb.Name || "").trim().toLowerCase() === bName.toLowerCase()
              );
              if (found) matchedBranchId = Number(found.Id);
              else if (bIdx === 0 && parseBool(bData.IsMainBranch)) {
                const mainFound = existingBranches.find((eb) => eb.IsMainBranch === 1 || eb.IsMainBranch === true);
                if (mainFound) matchedBranchId = Number(mainFound.Id);
              }
            }

            const isMain = parseBool(bData.IsMainBranch) || (bIdx === 0 && !hasAnyMainSpecified);

            return {
              Id: matchedBranchId || 0,
              Name: bName,
              CoAddress: bData.CoAddress ? String(bData.CoAddress).trim() : "",
              CoOtherAddress: bData.CoOtherAddress ? String(bData.CoOtherAddress).trim() : "",
              OfficeAddress: bData.OfficeAddress ? String(bData.OfficeAddress).trim() : "",
              OfficeAddressOther: bData.OfficeAddressOther ? String(bData.OfficeAddressOther).trim() : "",
              PhoneMobileNo: bData.PhoneMobileNo ? String(bData.PhoneMobileNo).trim() : "",
              IsMainBranch: isMain ? 1 : 0,
              CentralRuleMinWages: parseBool(bData.CentralRuleMinWages) ? 1 : 0,
              PFNo: bData.PFNo ? String(bData.PFNo).trim() : "",
              ESINo: bData.ESINo ? String(bData.ESINo).trim() : "",
              LINNo: bData.LINNo ? String(bData.LINNo).trim() : "",
              FBRegNo: bData.FBRegNo ? String(bData.FBRegNo).trim() : "",
              RegRenDate: safeDate(bData.RegRenDate) || "1900-01-01",
              RegExpiryDate: safeDate(bData.RegExpiryDate) || "1900-01-01",
              Connection: bData.Connection ? String(bData.Connection).trim() : "",
              FirmNameHindi: bData.FirmNameHindi ? String(bData.FirmNameHindi).trim() : "",
              FirmAddressHindi: bData.FirmAddressHindi ? String(bData.FirmAddressHindi).trim() : "",
              ManagerName: bData.ManagerName ? String(bData.ManagerName).trim() : "",
              ManagerAddress: bData.ManagerAddress ? String(bData.ManagerAddress).trim() : "",
              IsContractor: parseBool(bData.IsContractor) ? 1 : 0,
              PrincipleEmployer: bData.PrincipleEmployer ? String(bData.PrincipleEmployer).trim() : "",
              PrincipleEmployerAddress: bData.PrincipleEmployerAddress ? String(bData.PrincipleEmployerAddress).trim() : "",
              PFUserName: bData.PFUserName ? String(bData.PFUserName).trim() : "",
              PFPassword: bData.PFPassword ? String(bData.PFPassword).trim() : "",
              ESIUserName: bData.ESIUserName ? String(bData.ESIUserName).trim() : "",
              ESIPassword: bData.ESIPassword ? String(bData.ESIPassword).trim() : "",
              FactoryActUserName: bData.FactoryActUserName ? String(bData.FactoryActUserName).trim() : "",
              FactoryActPassword: bData.FactoryActPassword ? String(bData.FactoryActPassword).trim() : "",
              EmploymentUserName: bData.EmploymentUserName ? String(bData.EmploymentUserName).trim() : "",
              EmploymentPassword: bData.EmploymentPassword ? String(bData.EmploymentPassword).trim() : "",
              LabourUserName: bData.LabourUserName ? String(bData.LabourUserName).trim() : "",
              LabourPassword: bData.LabourPassword ? String(bData.LabourPassword).trim() : "",
              OSHUserName: bData.OSHUserName ? String(bData.OSHUserName).trim() : "",
              OSHPassword: bData.OSHPassword ? String(bData.OSHPassword).trim() : "",
              ShramSuvidhaUserName: bData.ShramSuvidhaUserName ? String(bData.ShramSuvidhaUserName).trim() : "",
              ShramSuvidhaPassword: bData.ShramSuvidhaPassword ? String(bData.ShramSuvidhaPassword).trim() : "",
              SSOUserName: bData.SSOUserName ? String(bData.SSOUserName).trim() : "",
              SSOPassword: bData.SSOPassword ? String(bData.SSOPassword).trim() : "",
            };
          });

          // Check if any row in group is new or modified
          const anyModified = groupItems.some((it) => it.isNew || it.isModified);
          if (!anyModified && targetId > 0) {
            unchangedCount += groupItems.length;
            groupItems.forEach((item) => {
              const previewIdx = updatedPreview.findIndex((p) => p.rowIndex === item.rowIndex);
              if (previewIdx !== -1) {
                updatedPreview[previewIdx] = {
                  ...updatedPreview[previewIdx],
                  status: "success",
                  apiAction: "UNCHANGED",
                  apiError: null,
                };
              }
              details.push({
                RowIndex: item.rowIndex,
                Name: `${rowMatchName} (${item.data.BranchName || "Head Office"})`,
                Status: "SUCCESS",
                Action: "UNCHANGED",
              });
            });
            continue;
          }

          try {
            const formData = config.toFormData(firmData, firmId, branchId, foreignMaps, existingRecord, branchRows);
            const saveUrl = targetId > 0
              ? `${API_WEB_URLS.BASE}${config.saveApi}/${targetId}`
              : `${API_WEB_URLS.BASE}${config.saveApi}`;
            const saveMethod = targetId > 0 ? "PUT" : "POST";

            const headers = {};
            if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

            const saveRes = await fetch(saveUrl, {
              method: saveMethod,
              headers,
              body: formData,
            });

            let isSuccess = false;
            let errMsg = "";
            let finalAction = targetId > 0 ? "UPDATED" : "INSERTED";

            if (saveRes.ok) {
              const json = await saveRes.json().catch(() => null);
              const resData = json?.response ? json.response[0] : (json?.data?.data || json?.data || json);
              const resId = resData?.Id ?? resData?.id ?? (typeof resData === "number" ? resData : null);

              if (resId === -1) errMsg = "Record with this name already exists";
              else if (resId === -2) errMsg = "Required details missing";
              else if (resId > 0 || json?.success || json?.status === 200 || saveRes.status === 200) isSuccess = true;
              else errMsg = json?.message || "Failed to save record";
            } else {
              const errJson = await saveRes.json().catch(() => null);
              errMsg = errJson?.message || `Server returned HTTP ${saveRes.status}`;
            }

            if (isSuccess) {
              if (targetId > 0) updatedCount += groupItems.length;
              else insertedCount += groupItems.length;
              groupItems.forEach((item) => {
                const previewIdx = updatedPreview.findIndex((p) => p.rowIndex === item.rowIndex);
                if (previewIdx !== -1) {
                  updatedPreview[previewIdx] = {
                    ...updatedPreview[previewIdx],
                    status: "success",
                    apiAction: finalAction,
                    apiError: null,
                  };
                }
                details.push({
                  RowIndex: item.rowIndex,
                  Name: `${rowMatchName} (${item.data.BranchName || "Head Office"})`,
                  Status: "SUCCESS",
                  Action: finalAction,
                });
              });
            } else {
              failedCount += groupItems.length;
              groupItems.forEach((item) => {
                const previewIdx = updatedPreview.findIndex((p) => p.rowIndex === item.rowIndex);
                if (previewIdx !== -1) {
                  updatedPreview[previewIdx] = {
                    ...updatedPreview[previewIdx],
                    status: "failed",
                    apiAction: "FAILED",
                    apiError: errMsg,
                  };
                }
                errors.push(`Row ${item.rowIndex} (${rowMatchName} - ${item.data.BranchName || "Branch"}): ${errMsg}`);
                details.push({
                  RowIndex: item.rowIndex,
                  Name: `${rowMatchName} (${item.data.BranchName || "Head Office"})`,
                  Status: "FAILED",
                  Action: "FAILED",
                  Error: errMsg,
                });
              });
            }
          } catch (netErr) {
            failedCount += groupItems.length;
            const errMsg = netErr.message || "Network request failed";
            groupItems.forEach((item) => {
              const previewIdx = updatedPreview.findIndex((p) => p.rowIndex === item.rowIndex);
              if (previewIdx !== -1) {
                updatedPreview[previewIdx] = {
                  ...updatedPreview[previewIdx],
                  status: "failed",
                  apiAction: "FAILED",
                  apiError: errMsg,
                };
              }
              errors.push(`Row ${item.rowIndex} (${rowMatchName}): ${errMsg}`);
            });
          }

          setImportProgress(Math.round(10 + ((gIdx + 1) / totalGroups) * 88));
        }
      } else {
        for (let i = 0; i < totalToProcess; i++) {
        const item = validRows[i];
        const rowData = item.data;
        const targetId = item.existingId || (!item.isNew && rowData.Id ? Number(rowData.Id) : 0) || 0;
        const rowMatchName = rowData[config.matchKey] || rowData.Name || rowData.ExtraPayName || `Row ${item.rowIndex}`;
        const previewIdx = updatedPreview.findIndex((p) => p.rowIndex === item.rowIndex);

        // If record is existing and has no field modifications, skip save call and keep as UNCHANGED
        if (item.type === "EXISTING" || (!item.isNew && !item.isModified)) {
          unchangedCount++;
          if (previewIdx !== -1) {
            updatedPreview[previewIdx] = {
              ...updatedPreview[previewIdx],
              status: "success",
              apiAction: "UNCHANGED",
              apiError: null,
            };
          }
          details.push({
            RowIndex: item.rowIndex,
            Name: rowMatchName,
            Status: "SUCCESS",
            Action: "UNCHANGED",
          });
          continue;
        }

        try {
          const formData = config.toFormData(rowData, firmId, branchId, foreignMaps, item.existingRecord);

          const saveUrl = targetId > 0
            ? `${API_WEB_URLS.BASE}${config.saveApi}/${targetId}`
            : `${API_WEB_URLS.BASE}${config.saveApi}`;
          const saveMethod = targetId > 0 ? "PUT" : "POST";

          const headers = {};
          if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

          const saveRes = await fetch(saveUrl, {
            method: saveMethod,
            headers,
            body: formData,
          });

          let isSuccess = false;
          let errMsg = "";
          let finalAction = targetId > 0 ? "UPDATED" : "INSERTED";

          if (saveRes.ok) {
            const json = await saveRes.json().catch(() => null);
            const resData = json?.response ? json.response[0] : (json?.data?.data || json?.data || json);
            const resId = resData?.Id ?? resData?.id ?? (typeof resData === "number" ? resData : null);

            if (resId === -1) {
              errMsg = "Record with this name already exists";
            } else if (resId === -2) {
              errMsg = "Required details missing";
            } else if (resId > 0 || json?.success || json?.status === 200 || saveRes.status === 200) {
              isSuccess = true;
            } else {
              errMsg = json?.message || "Failed to save record";
            }
          } else {
            const errJson = await saveRes.json().catch(() => null);
            if (saveRes.status === 404 && (errJson?.message?.toLowerCase().includes("already exists") || errJson?.status === 208)) {
              errMsg = "Record with this name already exists";
            } else {
              errMsg = errJson?.message || `Server returned HTTP ${saveRes.status}`;
            }
          }

          if (isSuccess) {
            if (targetId > 0) {
              updatedCount++;
            } else {
              insertedCount++;
            }
            if (previewIdx !== -1) {
              updatedPreview[previewIdx] = {
                ...updatedPreview[previewIdx],
                status: "success",
                apiAction: finalAction,
                apiError: null,
              };
            }
            details.push({
              RowIndex: item.rowIndex,
              Name: rowMatchName,
              Status: "SUCCESS",
              Action: finalAction,
            });
          } else {
            failedCount++;
            errors.push(`Row ${item.rowIndex} (${rowMatchName}): ${errMsg}`);
            if (previewIdx !== -1) {
              updatedPreview[previewIdx] = {
                ...updatedPreview[previewIdx],
                status: "failed",
                apiError: errMsg,
              };
            }
            details.push({
              RowIndex: item.rowIndex,
              Name: rowMatchName,
              Status: "FAILED",
              Message: errMsg,
            });
          }
        } catch (rowErr) {
          failedCount++;
          const errMsg = String(rowErr?.message || rowErr || "Error executing save");
          errors.push(`Row ${item.rowIndex} (${rowMatchName}): ${errMsg}`);
          if (previewIdx !== -1) {
            updatedPreview[previewIdx] = {
              ...updatedPreview[previewIdx],
              status: "failed",
              apiError: errMsg,
            };
          }
          details.push({
            RowIndex: item.rowIndex,
            Name: rowMatchName,
            Status: "FAILED",
            Message: errMsg,
          });
        }

        // Dynamic progress update
        const progressVal = Math.round(10 + ((i + 1) / totalToProcess) * 88);
        setImportProgress(progressVal);
      }
    }

      setPreviewData(updatedPreview);
      setImportProgress(100);

      const totalSuccess = insertedCount + updatedCount + unchangedCount;
      setImportResults({
        total: validRows.length,
        success: totalSuccess,
        inserted: insertedCount,
        updated: updatedCount,
        unchanged: unchangedCount,
        failed: failedCount,
        errors,
        details,
      });

      setShowResultModal(true);
      loadExistingData();

      if (insertedCount > 0 || updatedCount > 0) {
        toast.success(`🎉 Import Complete! ${insertedCount} inserted, ${updatedCount} updated${unchangedCount > 0 ? `, ${unchangedCount} unchanged` : ""}.`);
      } else if (unchangedCount > 0) {
        toast.info(`ℹ️ All ${unchangedCount} records were already up-to-date in database.`);
      }
      if (failedCount > 0) {
        toast.error(`❌ ${failedCount} records failed during import.`);
      }
    } catch (err) {
      console.error("Direct import error:", err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setPreviewData([]);
    setShowPreview(false);
    setImportProgress(0);
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ──────────────────────────────────────────
  // UI Badges (Matching EmployeeMaster)
  // ──────────────────────────────────────────
  const StatusBadge = ({ status, action }) => {
    const map = {
      pending: { bg: "#6c757d", label: "Pending" },
      success: {
        bg: action === "INSERTED" ? "#059669" : action === "UPDATED" ? "#2563eb" : "#64748b",
        label: action === "INSERTED" ? "✓ Inserted" : action === "UPDATED" ? "✓ Updated" : "✓ Unchanged",
      },
      failed: { bg: "#dc3545", label: "✗ Failed" },
      skipped: { bg: "#fd7e14", label: "⚠ Skipped" },
      error: { bg: "#dc3545", label: "✗ Invalid" },
    };
    const info = map[status] || map["pending"];
    return (
      <span
        style={{
          background: info.bg,
          color: "#fff",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          display: "inline-block",
        }}
      >
        {info.label}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    if (type === "INSERT") {
      return (
        <span
          style={{
            background: "#e0e7ff",
            color: "#3730a3",
            padding: "3px 8px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            display: "inline-block",
          }}
        >
          + INSERT
        </span>
      );
    }
    if (type === "UPDATE") {
      return (
        <span
          style={{
            background: "#fef3c7",
            color: "#92400e",
            padding: "3px 8px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            display: "inline-block",
          }}
        >
          ✏️ UPDATE
        </span>
      );
    }
    return (
      <span
        style={{
          background: "#f1f5f9",
          color: "#64748b",
          border: "1px solid #e2e8f0",
          padding: "3px 8px",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          display: "inline-block",
        }}
      >
        — EXISTING
      </span>
    );
  };

  const validCount = previewData.filter((r) => r.status !== "error").length;
  const errorCount = previewData.filter((r) => r.status === "error").length;
  const insertCount = previewData.filter((r) => r.status !== "error" && r.type === "INSERT").length;
  const updateCount = previewData.filter((r) => r.status !== "error" && r.type === "UPDATE").length;
  const existingCount = previewData.filter((r) => r.status !== "error" && r.type === "EXISTING").length;

  return (
    <div style={{ padding: "20px 24px", minHeight: "100vh", background: "#f4f6fb", fontFamily: "'Poppins', sans-serif" }}>
      {/* ── TOP HEADER ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          background: "#fff",
          padding: "16px 24px",
          borderRadius: 14,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(-1)}
            style={{ borderRadius: 8, padding: "6px 14px", fontWeight: 600 }}
          >
            ← Back
          </button>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, color: "#1e293b", fontSize: 20 }}>
              📊 {config.title} — Import / Export
            </h3>
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {config.isFirmSpecific
                ? `Firm: ${selectedFirm?.FirmName || selectedFirm?.Name || "Active Firm"}`
                : "Global Master Level"}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-outline-info btn-sm"
            style={{ borderRadius: 8, fontWeight: 600 }}
            onClick={() => setShowGuidelinesModal(true)}
          >
            ℹ️ Field Guidelines
          </button>
        </div>
      </div>

      {/* ── DUAL-CARD GRID (EXPORT & IMPORT) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: 24,
          marginBottom: 28,
        }}
      >
        {/* ── 1. EXPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #059669, #10b981)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📤 Export {config.title}
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                {config.isFirmSpecific ? selectedFirm?.FirmName || selectedFirm?.Name || "Selected Firm" : "Active Database Records"}
              </p>
            </div>
            {existingList.length > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,0.22)",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {existingList.length} Records
              </span>
            )}
          </div>

          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 10,
                  padding: "12px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#166534", fontWeight: 600, textTransform: "uppercase" }}>
                  Total Records
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#059669" }}>
                  {loadingSummary ? "..." : existingList.length}
                </div>
              </div>

              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 10,
                  padding: "12px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 600, textTransform: "uppercase" }}>
                  Exportable Fields
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#2563eb" }}>
                  {config.fields.length}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#fafafa",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 20,
                border: "1px solid #f0f0f5",
                fontSize: 13,
                color: "#4b5563",
                lineHeight: 1.6,
              }}
            >
              ✨ Full styled Excel export with automated formatting (<strong>YES/NO</strong>, <strong>YYYY-MM-DD</strong>, and clean headers).
            </div>

            <div style={{ marginTop: "auto" }}>
              <button
                className="btn"
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "14px 20px",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  cursor: exporting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
                  transition: "all 0.2s",
                }}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Generating & Downloading Excel...
                  </>
                ) : (
                  "📥 Export & Download Excel"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── 2. IMPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📥 Import {config.title} (Auto Insert / Update)
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                Upload Excel file — matches automatically by <strong>{config.matchKey}</strong>
              </p>
            </div>
            <button
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => setShowGuidelinesModal(true)}
            >
              ℹ️ Guidelines
            </button>
          </div>

          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Template Download Banner */}
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#166534" }}>
                  💡 Need the template format?
                </div>
                <div style={{ fontSize: 12, color: "#15803d", marginTop: 2 }}>
                  Download the blank template with column headings only (no data rows).
                </div>
              </div>
              <button
                className="btn"
                style={{
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: downloadingTemplate ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
              >
                {downloadingTemplate ? "⏳ Downloading..." : "📄 Download Template"}
              </button>
            </div>

            {/* Auto Match Info */}
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 12,
                color: "#1e40af",
                lineHeight: 1.5,
              }}
            >
              <strong>⚡ Auto-Matching Rule:</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                <li><strong>Existing Record:</strong> If <code>{config.matchKey}</code> exists → <strong>Updates</strong> existing record.</li>
                <li><strong>New Record:</strong> If <code>{config.matchKey}</code> is new → <strong>Inserts</strong> a new record.</li>
              </ul>
            </div>

            {/* Dropzone */}
            <div
              style={{
                border: "2px dashed #cbd5e1",
                borderRadius: 12,
                padding: "24px 20px",
                textAlign: "center",
                background: "#f8fafc",
                cursor: "pointer",
                marginBottom: 16,
                transition: "border-color 0.2s",
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const fakeEvent = { target: { files: [file], value: "" } };
                  handleFileChange(fakeEvent);
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              {importFile ? (
                <div>
                  <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>
                    {importFile.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {(importFile.size / 1024).toFixed(1)} KB — Click to change file
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, color: "#334155", fontSize: 14 }}>
                    Drop your Excel file here, or{" "}
                    <span style={{ color: "#2563eb", textDecoration: "underline" }}>browse</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    Supports .xlsx and .xls
                  </div>
                </div>
              )}
            </div>

            {isParsing && (
              <div style={{ textAlign: "center", padding: "10px 0", color: "#2563eb", fontSize: 13 }}>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Parsing Excel file...
              </div>
            )}

            {importFile && !isParsing && (
              <div style={{ display: "flex", gap: 10, marginTop: "auto" }}>
                <button
                  className="btn btn-outline-secondary"
                  style={{ borderRadius: 8, fontSize: 13, padding: "8px 16px" }}
                  onClick={resetImport}
                >
                  Clear
                </button>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                    color: "#fff",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 14,
                    border: "none",
                  }}
                  onClick={() => setShowPreview(true)}
                >
                  👁️ View Preview ({previewData.length} rows)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── PREVIEW & IMPORT SECTION ── */}
      {showPreview && previewData.length > 0 && (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            padding: 24,
            marginBottom: 28,
            border: "1px solid #e8e8f0",
          }}
        >
          {/* Header Row with Stats and Run Import Button */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, color: "#1e293b", fontSize: 17 }}>
                📋 Import Data Preview
              </h4>
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  Total: {previewData.length}
                </span>
                <span style={{ background: "#05966915", color: "#059669", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  Valid: {validCount}
                </span>
                <span style={{ background: "#e0e7ff", color: "#3730a3", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  + Insert: {insertCount}
                </span>
                <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  ✏️ Update: {updateCount}
                </span>
                {existingCount > 0 && (
                  <span style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    — Existing: {existingCount}
                  </span>
                )}
                {errorCount > 0 && (
                  <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    ✗ Errors: {errorCount}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: 8, fontWeight: 600 }}
                onClick={resetImport}
                disabled={importing}
              >
                Cancel
              </button>

              <button
                className="btn"
                style={{
                  background: validCount === 0 ? "#94a3b8" : "linear-gradient(135deg, #1d4ed8, #2563eb)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 24px",
                  fontWeight: 700,
                  fontSize: 14,
                  border: "none",
                  cursor: validCount === 0 || importing ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 14px rgba(29,78,216,0.35)",
                }}
                onClick={handleImport}
                disabled={validCount === 0 || importing}
              >
                {importing ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Importing ({importProgress}%)...
                  </>
                ) : (
                  `⚡ Run Import (${validCount} records)`
                )}
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {importing && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                <span>Processing records...</span>
                <span>{importProgress}%</span>
              </div>
              <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${importProgress}%`,
                    background: "linear-gradient(90deg, #1d4ed8, #3b82f6)",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}

          {/* Preview Table */}
          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
            <table className="table table-hover mb-0" style={{ fontSize: 13, borderCollapse: "collapse", width: "100%" }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ padding: "10px 12px", borderBottom: "2px solid #e2e8f0", width: 60, textAlign: "center" }}>#</th>
                  <th style={{ padding: "10px 12px", borderBottom: "2px solid #e2e8f0", width: 100 }}>Status</th>
                  <th style={{ padding: "10px 12px", borderBottom: "2px solid #e2e8f0", width: 140 }}>Type</th>
                  {config.fields.map((f) => (
                    <th key={f.key} style={{ padding: "10px 12px", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>
                      {f.label}
                    </th>
                  ))}
                  <th style={{ padding: "10px 12px", borderBottom: "2px solid #e2e8f0", minWidth: 160 }}>Validation / Errors</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 100).map((row, idx) => {
                  const isErr = row.status === "error";
                  return (
                    <tr
                      key={idx}
                      style={{
                        background: isErr ? "#fff5f5" : idx % 2 === 0 ? "#fff" : "#fafafa",
                        transition: "background 0.15s",
                      }}
                    >
                      <td
                        style={{ padding: "8px 12px", textAlign: "center", color: "#64748b", fontWeight: 600 }}
                        title={`Excel Row: ${row.rowIndex}`}
                      >
                        {row.sNo || idx + 1}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <StatusBadge status={row.status} action={row.apiAction} />
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <TypeBadge isNew={row.isNew} type={row.type} />
                      </td>
                      {config.fields.map((f) => (
                        <td key={f.key} style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                          {row.data[f.key] ?? "—"}
                        </td>
                      ))}
                      <td style={{ padding: "8px 12px" }}>
                        {row.errors && row.errors.length > 0 ? (
                          <div style={{ color: "#dc2626", fontSize: 11 }}>
                            {row.errors.join(", ")}
                          </div>
                        ) : row.apiError ? (
                          <div style={{ color: "#dc2626", fontSize: 11 }}>
                            {row.apiError}
                          </div>
                        ) : (
                          <span style={{ color: "#16a34a", fontSize: 11 }}>✓ Ready</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {previewData.length > 100 && (
            <div style={{ textAlign: "center", padding: "10px 0", color: "#64748b", fontSize: 12 }}>
              Showing first 100 rows of {previewData.length} total rows.
            </div>
          )}
        </div>
      )}

      {/* ── RESULT MODAL ── */}
      {showResultModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 600,
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                background: importResults.failed === 0 ? "linear-gradient(135deg, #059669, #10b981)" : "linear-gradient(135deg, #1e40af, #3b82f6)",
                padding: "20px 24px",
                color: "#fff",
              }}
            >
              <h4 style={{ margin: 0, fontWeight: 700 }}>
                {importResults.failed === 0 ? "🎉 Import Completed Successfully!" : "📊 Import Completed with Notices"}
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                {config.title} Summary
              </p>
            </div>

            <div style={{ padding: 24 }}>
              {/* Summary Cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Total</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{importResults.total}</div>
                </div>
                <div style={{ background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#15803d", fontWeight: 600 }}>Inserted</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#15803d" }}>{importResults.inserted}</div>
                </div>
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>Updated</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#d97706" }}>{importResults.updated}</div>
                </div>
                <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>Unchanged</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#64748b" }}>{importResults.unchanged ?? 0}</div>
                </div>
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>Failed</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>{importResults.failed}</div>
                </div>
              </div>

              {/* Error list */}
              {importResults.errors && importResults.errors.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#991b1b", marginBottom: 6 }}>
                    ⚠️ Failed Items ({importResults.errors.length}):
                  </div>
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: "auto",
                      background: "#fff5f5",
                      border: "1px solid #fecaca",
                      borderRadius: 8,
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "#b91c1c",
                    }}
                  >
                    {importResults.errors.map((err, i) => (
                      <div key={i} style={{ marginBottom: 4 }}>• {err}</div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  className="btn btn-primary"
                  style={{ borderRadius: 8, padding: "8px 24px", fontWeight: 600 }}
                  onClick={() => setShowResultModal(false)}
                >
                  OK / Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── GUIDELINES MODAL ── */}
      {showGuidelinesModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1060,
            padding: 20,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 750,
              width: "100%",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                padding: "18px 24px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>
                  ℹ️ {config.title} — Excel Import Guidelines
                </h4>
                <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 12 }}>
                  Standard formats, rules, and auto-matching definitions
                </p>
              </div>
              <button
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: 22,
                  cursor: "pointer",
                }}
                onClick={() => setShowGuidelinesModal(false)}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0" }}>
              <input
                type="text"
                placeholder="Search guidelines / fields..."
                className="form-control"
                value={guidelineSearch}
                onChange={(e) => setGuidelineSearch(e.target.value)}
                style={{ borderRadius: 8, fontSize: 13 }}
              />
            </div>

            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {config.guidelines.map((cat, ci) => {
                const filteredFields = cat.fields.filter(
                  (f) =>
                    !guidelineSearch ||
                    f.field.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
                    f.rule.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
                    f.example.toLowerCase().includes(guidelineSearch.toLowerCase())
                );
                if (filteredFields.length === 0) return null;

                return (
                  <div key={ci} style={{ marginBottom: 24 }}>
                    <h5 style={{ color: "#1e293b", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                      {cat.category}
                    </h5>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {filteredFields.map((f, fi) => (
                        <div
                          key={fi}
                          style={{
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "10px 14px",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: 700, color: "#1e40af", fontSize: 13 }}>
                              {f.field}
                            </span>
                            <span
                              style={{
                                background: "#e2e8f0",
                                color: "#475569",
                                borderRadius: 4,
                                padding: "2px 6px",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              Format: {f.format}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                            {f.rule}
                          </div>
                          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                            <strong>Allowed:</strong> {f.allowed} &nbsp;|&nbsp; <strong>Example:</strong>{" "}
                            <code>{f.example}</code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: "14px 24px", borderTop: "1px solid #e2e8f0", textAlign: "right" }}>
              <button
                className="btn btn-secondary btn-sm"
                style={{ borderRadius: 8, padding: "6px 18px", fontWeight: 600 }}
                onClick={() => setShowGuidelinesModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PRE-CONFIGURED ROUTE EXPORTS FOR EACH MASTER
// ─────────────────────────────────────────────────────────────
export const DepartmentImportExport = () => <MasterImportExport masterKey="DepartmentMaster" />;
export const DesignationImportExport = () => <MasterImportExport masterKey="DesignationMaster" />;
export const FirmImportExport = () => <MasterImportExport masterKey="FirmMaster" />;
export const FirmTypeImportExport = () => <MasterImportExport masterKey="FirmTypeMaster" />;
export const ConcernTypeImportExport = () => <MasterImportExport masterKey="ConcernTypeMaster" />;
export const PremisesTypeImportExport = () => <MasterImportExport masterKey="PremisesTypeMaster" />;
export const WorkerCategoryImportExport = () => <MasterImportExport masterKey="WorkerCategory" />;
export const EmployeeTypeImportExport = () => <MasterImportExport masterKey="EmployeeTypeMaster" />;
export const ReasonOfLeftImportExport = () => <MasterImportExport masterKey="ReasonOfLeftMaster" />;
export const LeaveImportExport = () => <MasterImportExport masterKey="LeaveMaster" />;
export const AllowanceImportExport = () => <MasterImportExport masterKey="AllowanceMaster" />;
export const DeductionImportExport = () => <MasterImportExport masterKey="DeductionMaster" />;
export const ExtraPayImportExport = () => <MasterImportExport masterKey="ExtraPayMaster" />;

export default MasterImportExport;
